import React, { useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Campaign,
  Visibility,
  Receipt,
  CurrencyRupee,
  Handshake,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  TabPanel,
  StyledTabs,
  KPICard,
  SectionHeader,
  ChartCard,
  DataTable,
  StatusChip,
  COLORS,
  CHART_COLORS,
  PageContainer,
} from './components/ReportShared';
import DateRangeFilter, { getDefaultDateRange } from './components/DateRangeFilter';
import useReportData from './hooks/useReportData';
import { fetchSourceVisits, fetchSourceBookings, fetchCPPerformance } from '../../services/reportV2Service';

export default function SourceChannelPerformance() {
  const [tabValue, setTabValue] = useState(0);
  const [visitsDateRange, setVisitsDateRange] = useState(getDefaultDateRange());
  const [bookingsDateRange, setBookingsDateRange] = useState(getDefaultDateRange());
  const [cpDateRange, setCpDateRange] = useState(getDefaultDateRange());

  const { data: visitsData, loading: visitsLoading } = useReportData(
    fetchSourceVisits, visitsDateRange, {}, tabValue === 0,
  );
  const { data: bookingsData, loading: bookingsLoading } = useReportData(
    fetchSourceBookings, bookingsDateRange, {}, tabValue === 1,
  );
  const { data: cpData, loading: cpLoading } = useReportData(
    fetchCPPerformance, cpDateRange, {}, tabValue === 2,
  );

  const handleTabChange = (_, newValue) => setTabValue(newValue);

  const TABS = [
    'Source-wise Site Visits',
    'Source-wise Bookings',
    'Channel Partner Performance',
  ];

  const formatCurrency = (val) => {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(0)}K`;
    return val ? `₹${val}` : '—';
  };

  const LoadingState = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
      <CircularProgress sx={{ color: COLORS.teal[600] }} />
    </Box>
  );

  const EmptyState = () => (
    <Typography sx={{ textAlign: 'center', py: 6, color: COLORS.slate[400] }}>No data available</Typography>
  );

  return (
    <PageContainer>
      <StyledTabs value={tabValue} onChange={handleTabChange} tabs={TABS} />

      {/* Tab 1: Source-wise Site Visits & Visit-to-Booking Rate */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Source-wise Site Visits
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Leads, visits, bookings and visit-to-booking rate by source
            </Typography>
          </Box>
          <DateRangeFilter value={visitsDateRange} onChange={setVisitsDateRange} />
        </Box>

        {visitsLoading ? <LoadingState /> : !visitsData ? <EmptyState /> : (() => {
          const kpis = visitsData.kpis || {};
          return (
            <>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <KPICard title="Total Leads" value={(kpis.totalLeads ?? 0).toLocaleString()} icon={Campaign} color={COLORS.teal[600]} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <KPICard title="Total Visits" value={(kpis.totalVisits ?? 0).toLocaleString()} icon={Visibility} color={COLORS.teal[600]} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <KPICard title="Best Visit Source" value={kpis.bestVisitSource || '-'} icon={Campaign} color={COLORS.blue} />
                </Grid>
              </Grid>

              <Box sx={{ mt: 4 }}>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <KPICard title="Avg Visit Rate" value={`${kpis.avgVisitRate ?? 0}%`} icon={Visibility} color={COLORS.purple} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <KPICard title="Overall V2B Rate" value={`${kpis.overallV2BRate ?? 0}%`} icon={Receipt} color={COLORS.teal[600]} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <KPICard
                      title="Best V2B Source"
                      value={`${kpis.bestV2BSource || '-'} ${kpis.bestV2BRate ? kpis.bestV2BRate + '%' : ''}`}
                      icon={Campaign}
                      color={COLORS.green}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <DataTable
                      columns={[
                        { label: 'Source', field: 'source' },
                        { label: 'Leads', field: 'leads', align: 'right' },
                        { label: 'Visits', field: 'visits', align: 'right' },
                        { label: 'Bookings', field: 'bookings', align: 'right' },
                        { label: 'V2B Rate %', field: 'v2bRate', align: 'right', render: (v) => `${v}%` },
                      ]}
                      rows={visitsData.table || []}
                    />
                  </Grid>
                </Grid>
              </Box>
            </>
          );
        })()}
      </TabPanel>

      {/* Tab 2: Source-wise Bookings */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Source-wise Bookings
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Booking volume and value by source
            </Typography>
          </Box>
          <DateRangeFilter value={bookingsDateRange} onChange={setBookingsDateRange} />
        </Box>

        {bookingsLoading ? <LoadingState /> : !bookingsData ? <EmptyState /> : (() => {
          const kpis = bookingsData.kpis || {};
          return (
            <>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <KPICard title="Total Bookings" value={(kpis.totalBookings ?? 0).toLocaleString()} icon={Receipt} color={COLORS.teal[600]} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <KPICard title="Best Booking Source" value={kpis.bestBookingSource || '-'} icon={Campaign} color={COLORS.blue} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <KPICard title="Avg Booking Value" value={formatCurrency(kpis.avgBookingValue ?? 0)} icon={CurrencyRupee} color={COLORS.purple} />
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ChartCard title="Booking Distribution" subtitle="Share by source" height={320}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={bookingsData.pieData || []}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {(bookingsData.pieData || []).map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => [v, 'Bookings']} contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SectionHeader title="Booking Value Details" subtitle="Bookings, value and avg per source" />
                  <DataTable
                    columns={[
                      { label: 'Source', field: 'source' },
                      { label: 'Bookings', field: 'bookings', align: 'right' },
                      { label: 'Value', field: 'value', align: 'right', render: (v) => formatCurrency(v) },
                      { label: 'Avg Value', field: 'avgValue', align: 'right', render: (v) => formatCurrency(v) },
                    ]}
                    rows={bookingsData.table || []}
                  />
                </Grid>
              </Grid>
            </>
          );
        })()}
      </TabPanel>

      {/* Tab 3: Channel Partner Performance */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Channel Partner Performance
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              CP leads, visits, bookings and commission
            </Typography>
          </Box>
          <DateRangeFilter value={cpDateRange} onChange={setCpDateRange} />
        </Box>

        {cpLoading ? <LoadingState /> : !cpData ? <EmptyState /> : (() => {
          const kpis = cpData.kpis || {};
          return (
            <>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <KPICard title="Total CPs" value={(kpis.totalCPs ?? 0).toLocaleString()} icon={Handshake} color={COLORS.teal[600]} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <KPICard title="Active CPs" value={(kpis.activeCPs ?? 0).toLocaleString()} icon={Handshake} color={COLORS.blue} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <KPICard title="CP Leads" value={(kpis.cpLeads ?? 0).toLocaleString()} icon={Campaign} color={COLORS.purple} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <KPICard title="CP Bookings" value={(kpis.cpBookings ?? 0).toLocaleString()} icon={Receipt} color={COLORS.green} />
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <SectionHeader title="CP-wise Leads" subtitle="Leads, qualified, visit rate and booking rate" />
                  <DataTable
                    columns={[
                      { label: 'CP Name', field: 'cp' },
                      { label: 'Leads', field: 'leads', align: 'right' },
                      { label: 'Qualified', field: 'qualified', align: 'right' },
                      { label: 'Visit Rate %', field: 'visitRate', align: 'right', render: (v) => `${v}%` },
                      { label: 'Bookings', field: 'bookings', align: 'right' },
                      { label: 'Booking Rate %', field: 'bookingRate', align: 'right', render: (v) => `${v}%` },
                    ]}
                    rows={cpData.leadsTable || []}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <SectionHeader title="CP Commission" subtitle="Bookings, commission rate, total and payout status" />
                  <DataTable
                    columns={[
                      { label: 'CP Name', field: 'cp' },
                      { label: 'Bookings', field: 'bookings', align: 'right' },
                      { label: 'Commission Rate', field: 'commissionRate', align: 'right', render: (v) => `${v}%` },
                      { label: 'Total Commission', field: 'totalCommission', align: 'right', render: (v) => formatCurrency(v) },
                      {
                        label: 'Payout Status',
                        field: 'payoutStatus',
                        align: 'center',
                        render: (v) => <StatusChip label={v} color={v === 'NA' ? COLORS.slate[400] : v === 'Paid' ? COLORS.green : COLORS.orange} />,
                      },
                    ]}
                    rows={cpData.commissionTable || []}
                  />
                </Grid>
              </Grid>
            </>
          );
        })()}
      </TabPanel>
    </PageContainer>
  );
}
